import EditButton from './EditButton'
import {FC, useEffect, useState} from 'react'
import {toAbsoluteUrl} from '_metronic/helpers'
import Card from './Card'
import AddEditFrameworkModal from './modals/AddEditFrameworkModal'
import {useAuth} from 'app/modules/auth'
import {collection, deleteDoc, addDoc, doc, serverTimestamp} from '@firebase/firestore'
import db, {storage} from 'services/firebase'
import {deleteObject, ref} from 'firebase/storage'
import {ToastContainer} from 'react-toastify'
import Button from 'app/components/Button'
import {showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import CustomDrawer from './Drawer'

export type FrameworkDataType = {
  id: string
  title: string
  logoName: string
  logoURL: string | File
  status: string
  logoId?: string
  isCustom?: boolean
}

type Props = {
  addedFrameworks: FrameworkDataType[]
}

const availableFrameworks = [
  {
    id: '1',
    title: 'ISO 27000:2022',
    logoURL: '/media/framework/ISO27001.svg',
  },
  {
    id: '2',
    title: 'SOC 2',
    logoURL: '/media/framework/SOC2.svg',
  },
  {
    id: '3',
    title: 'GDPR',
    logoURL: '/media/framework/GDPR.svg',
  },
  {
    id: '4',
    title: 'ISO 9001:2015',
    logoURL: '/media/framework/ISO9001.svg',
  },
  {
    id: '5',
    title: 'HIPAA',
    logoURL: '/media/framework/HIPAA.svg',
  },
  {
    id: '6',
    title: 'PCI DSS',
    logoURL: '/media/framework/PCIDSS.svg',
  },
]

const Compliance: FC<Props> = ({addedFrameworks}) => {
  const {currentUser} = useAuth()

  const [defaultFrameworks, setDefaultFrameworks] = useState<
    {
      id: string
      title: string
      logoURL: string
    }[]
  >([])

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [toEditFramework, setToEditFramework] = useState<FrameworkDataType>({
    id: '',
    title: '',
    logoName: '',
    logoURL: '',
    status: '',
  })

  const [toDeleteFramework, setToDeleteFramework] = useState<FrameworkDataType>()

  const [loading, setLoading] = useState(false)

  const onCancel = () => {
    setIsDrawerOpen(false)
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }

  useEffect(() => {
    const addedFrameworksTitleList = addedFrameworks.map((framework) =>
      framework.title.toLowerCase()
    )

    setDefaultFrameworks(
      availableFrameworks.filter(
        (framework) => !addedFrameworksTitleList.includes(framework.title.toLowerCase())
      )
    )
  }, [addedFrameworks])

  const handleRemoveFramework = async () => {
    setLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const frameworksRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'frameworks',
        'all_frameworks'
      )

      if (toDeleteFramework?.isCustom) {
        const fileRef = ref(
          storage,
          `tenants/${currentUser?.tenantId}/trust_center/frameworks/${toDeleteFramework.logoId}-${toDeleteFramework?.logoName}`
        )

        await deleteObject(fileRef)
      }

      await deleteDoc(doc(frameworksRef, toDeleteFramework?.id))

      const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      await addDoc(activitiesRef, {
        message: `${toDeleteFramework?.title} framework for trust center has been removed`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
        target: 'Trust Center',
        action: 'Framework removed',
      })

      showSuccessToast({message: 'Framework has been removed successfully'})
    } catch (error) {
      console.error('Error while removing framework: ', error)
    } finally {
      setLoading(false)
      setToDeleteFramework(undefined)
      setIsDeleteModalOpen(false)
    }
  }

  return (
    <div className='h-100'>
      <CustomDrawer isDrawerOpen={isDrawerOpen} onCancel={onCancel} title='Compliance'>
        <div
          style={{
            padding: '20px 20px 80px',
          }}
        >
          <p className='text-dark mb-6' style={{fontSize: '13px'}}>
            Manage the frameworks you want to display in your Trust Center. To add certifications
            and reports for these frameworks, upload them in the Documents.
          </p>

          <div className='d-flex flex-column'>
            <div className='d-flex justify-content-between align-items-center border-bottom border-gray-300 pb-1 mb-5'>
              <span className='text-dark fs-6 fw-bold'>Frameworks</span>

              <Button
                btnType='transparent'
                onClick={() => {
                  setToEditFramework({id: '', title: '', logoName: '', logoURL: '', status: ''})
                  setIsModalOpen(true)
                }}
              >
                <img
                  alt='plus-icon'
                  src={toAbsoluteUrl('/media/icon/plus.svg')}
                  className='me-2'
                  style={{height: '18px', width: '18px'}}
                />
                <span>Add Framework</span>
              </Button>
            </div>

            {addedFrameworks.length > 0 ? (
              addedFrameworks.map((framework, index) => (
                <div
                  key={framework.id}
                  className='d-flex align-items-center'
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    gap: '12px',
                    padding: '20px',
                    marginBottom: '12px',
                  }}
                >
                  <div className='flex-fill d-flex align-items-center' style={{gap: '6px'}}>
                    <img
                      alt={`${framework.title}-logo`}
                      src={toAbsoluteUrl(framework.logoURL as string)}
                      className='object-fit-cover'
                      style={{height: '18px', width: '18px'}}
                    />

                    <h4 className='text-dark fs-6 fw-medium mb-0'>{framework.title}</h4>
                  </div>

                  <div
                    className='d-flex align-items-center'
                    style={{
                      backgroundColor: `${
                        framework.status === 'Compliant' ? '#DEF7EC' : '#FEECDC'
                      }`,
                      border: `${
                        framework.status === 'Compliant' ? '1px solid #BCF0DA' : '1px solid #FCD9BD'
                      }`,
                      borderRadius: '6px',
                      gap: '4px',
                      padding: '2px 8px',
                    }}
                  >
                    <img
                      alt={`${framework.status}-logo`}
                      src={`${
                        framework.status === 'Compliant'
                          ? toAbsoluteUrl('/media/icon/circle-check.svg')
                          : toAbsoluteUrl('/media/icon/loader.svg')
                      }`}
                      className=''
                      style={{height: '14px', width: '14px'}}
                    />
                    <span
                      className='fw-medium text-center fs-7'
                      style={{
                        color: `${framework.status === 'Compliant' ? '#03543F' : '#8A2C0D'}`,
                      }}
                    >
                      {framework.status}
                    </span>
                  </div>

                  <div className='d-flex' style={{gap: '8px'}}>
                    <Button
                      style={{
                        width: '34px',
                        height: '34px',
                      }}
                      onClick={() => {
                        setToEditFramework(framework)
                        setIsModalOpen(true)
                      }}
                    >
                      <img
                        alt='edit-icon'
                        src={toAbsoluteUrl('/media/icon/edit.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>

                    <Button
                      style={{
                        width: '34px',
                        height: '34px',
                      }}
                      onClick={() => {
                        setIsDeleteModalOpen(true)
                        setToDeleteFramework(framework)
                      }}
                    >
                      <img
                        alt='trash-icon'
                        src={toAbsoluteUrl('/media/icon/trash.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div
                className='w-100 d-flex justify-content-center align-items-center'
                style={{height: '245px'}}
              >
                <div className='d-flex flex-column align-items-center gap-5'>
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#F3F4F6',
                      borderRadius: '12px',
                      height: '48px',
                      width: '48px',
                    }}
                  >
                    <img
                      alt='cube-icon'
                      src={toAbsoluteUrl('/media/icon/cube.svg')}
                      className=''
                      style={{height: '32px', width: '32px'}}
                    />
                  </div>

                  <span className='text-dark fs-6 fw-medium'>No frameworks added</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CustomDrawer>

      {isModalOpen && (
        <AddEditFrameworkModal
          isModalOpen={isModalOpen}
          hideModal={() => setIsModalOpen(false)}
          isEditMode={Boolean(toEditFramework.id)}
          framework={toEditFramework}
          availableFrameworks={defaultFrameworks}
        />
      )}

      <Card
        title='Compliance'
        actionComponent={
          <EditButton
            onClickHandler={() => {
              setIsDrawerOpen(true)
              const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
              document.body.style.overflow = 'hidden'
              document.body.style.paddingRight = `${scrollBarWidth}px`
            }}
          />
        }
      >
        {addedFrameworks.length > 0 ? (
          <div className='row gy-5 gx-10 me-0'>
            {addedFrameworks.map((framework, index) => (
              <div key={index} className='col-6 col-md-4 col-xl-6 col-xxl-4 pe-0'>
                <div
                  className='d-flex flex-column align-items-center'
                  style={{
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #F3F4F6',
                    borderRadius: '8px',
                    gap: '12px',
                    padding: '24px 16px',
                  }}
                >
                  <img
                    alt={`${framework.title}-logo`}
                    src={
                      typeof framework.logoURL === 'string'
                        ? toAbsoluteUrl(framework.logoURL)
                        : URL.createObjectURL(framework.logoURL)
                    }
                    className='object-fit-cover'
                    style={{height: '48px', width: '48px'}}
                  />

                  <span className='fw-medium text-dark text-center fs-6'>{framework.title}</span>

                  <div
                    className='d-flex align-items-center'
                    style={{
                      backgroundColor: `${
                        framework.status === 'Compliant' ? '#DEF7EC' : '#FEECDC'
                      }`,
                      border: `${
                        framework.status === 'Compliant' ? '1px solid #BCF0DA' : '1px solid #FCD9BD'
                      }`,
                      borderRadius: '6px',
                      gap: '4px',
                      padding: '2px 8px',
                    }}
                  >
                    <img
                      alt={`${framework.status}-logo`}
                      src={`${
                        framework.status === 'Compliant'
                          ? toAbsoluteUrl('/media/icon/circle-check.svg')
                          : toAbsoluteUrl('/media/icon/loader.svg')
                      }`}
                      className=''
                      style={{height: '14px', width: '14px'}}
                    />
                    <span
                      className='fw-medium text-center fs-7'
                      style={{
                        color: `${framework.status === 'Compliant' ? '#03543F' : '#8A2C0D'}`,
                      }}
                    >
                      {framework.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className='w-100 d-flex justify-content-center align-items-center'
            style={{height: '350px'}}
          >
            <div className='d-flex flex-column align-items-center gap-5'>
              <div
                className='d-flex align-items-center justify-content-center'
                style={{
                  backgroundColor: '#F3F4F6',
                  borderRadius: '12px',
                  height: '48px',
                  width: '48px',
                }}
              >
                <img
                  alt='cube-icon'
                  src={toAbsoluteUrl('/media/icon/cube.svg')}
                  className=''
                  style={{height: '32px', width: '32px'}}
                />
              </div>

              <span className='text-dark fs-6 fw-medium'>No frameworks added</span>
            </div>
          </div>
        )}
      </Card>

      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => setIsDeleteModalOpen(false)}
          confirmBtnClickHandler={handleRemoveFramework}
          toDeleteItemTitle='framework'
          isLoading={loading}
        />
      )}

      <ToastContainer />
    </div>
  )
}

export default Compliance
